import { projects, type GalleryItem, type Project } from "@/data/projects";
import type { SiteLanguageCode } from "@/lib/language";

type ProjectText = {
  title: string;
  category: string;
  description: string;
  solution: string;
  industry: string;
  duration?: string;
  challenge: string;
  approach: string;
  results: string[];
  galleryCaptions?: string[];
};

const categoryLabelsHi: Record<string, string> = {
  "Digital India Initiative": "डिजिटल इंडिया पहल",
  "Web Application": "वेब एप्लिकेशन",
  "E-Commerce": "ई-कॉमर्स",
  "Management System": "मैनेजमेंट सिस्टम",
  "Water Services": "जल सेवाएँ",
};

const projectsHi: Record<string, ProjectText> = {
  tarang: {
    title: "तरंग - FRA एप्लिकेशन",
    category: "डिजिटल इंडिया पहल",
    description:
      "भारत में जनजातीय भूमि अधिकार दावों को डिजिटाइज़ और प्रबंधित करने के लिए वन अधिकार अधिनियम (FRA) प्रबंधन प्रणाली।",
    solution: "वेब और मोबाइल एप्लिकेशन",
    industry: "भारत सरकार",
    duration: "2 वर्ष",
    challenge:
      "मंत्रालय को दूरस्थ जनजातीय क्षेत्रों में रुक-रुक कर कनेक्टिविटी के साथ वन अधिकार आवेदनों (FRA) का प्रबंधन करने के लिए एक मज़बूत, स्केलेबल सिस्टम चाहिए था। पीक आवेदन अवधियों में भारी समवर्ती ट्रैफ़िक संभालना ज़रूरी था।",
    approach:
      "हमने स्केलेबिलिटी के लिए माइक्रोसर्विस-आधारित डिस्ट्रिब्यूटेड क्लाउड समाधान बनाया। बैकएंड में FastAPI-संचालित समर्पित ML सेवाएँ शामिल हैं। मोबाइल ऐप में ऑफ़लाइन-प्रथम क्षमताएँ लागू कीं ताकि दूरस्थ क्षेत्रों में डेटा संग्रह हो और कनेक्टिविटी बहाल होने पर स्वतः सिंक हो।",
    results: [
      "20 लाख से अधिक आवेदन सहजता से प्रोसेस किए।",
      "आवेदन प्रोसेसिंग समय में 60% कमी।",
      "डिजिटाइज़्ड वर्कफ़्लो से 100% डेटा सटीकता।",
    ],
    galleryCaptions: [
      "लैंडिंग पेज",
      "लैंडिंग पेज",
      "RAG चैटबॉट",
      "आधिकारिक लॉगिन पेज",
      "वन डैशबोर्ड",
      "वन दावे पेज",
      "GIS-आधारित वन अधिकार एटलस",
      "GIS मैप",
      "MOTA डैशबोर्ड",
      "स्कीम गैलरी",
      "मोबाइल होम स्क्रीन",
      "मोबाइल लॉगिन पेज",
      "दावों की सूची",
      "मैप",
      "मोबाइल स्क्रीन",
      "मोबाइल ऐप स्क्रीन",
      "मोबाइल ऐप साइडबार",
      "मोबाइल प्रोफ़ाइल पेज",
    ],
  },
  "samku-ev-services": {
    title: "समकू EV सेवाएँ",
    category: "वेब एप्लिकेशन",
    description:
      "समकू सर्विसेज़ MERN स्टैक (MongoDB, Express.js, React.js, Node.js) पर बनी व्यापक EV चार्जिंग स्टेशन प्रबंधन प्लेटफ़ॉर्म है। इंस्टॉलेशन, मेंटनेंस सेवाएँ और EV उत्पादों की ऑनलाइन स्टोर सहित एंड-टू-एंड समाधान।",
    solution: "फुल स्टैक वेब एप्लिकेशन",
    industry: "EV और लॉजिस्टिक्स",
    duration: "8 महीने",
    challenge:
      "समकू EV को बैटरी स्वैपिंग और लॉजिस्टिक्स नेटवर्क अनुकूलित करना था। मैन्युअल रूटिंग अक्षम थी - उच्च परिचालन लागत और देरी।",
    approach:
      "हमने डिमांड पूर्वानुमान और रियल-टाइम रूट ऑप्टिमाइज़ेशन के लिए प्रोप्राइटरी न्यूरल नेटवर्क एकीकृत कस्टम लॉजिस्टिक्स प्लेटफ़ॉर्म बनाया। सिस्टम बैटरी स्वास्थ्य और स्थान ट्रैक करता है ताकि फ्लीट परफ़ॉर्मेंस बेहतर रहे।",
    results: [
      "लॉजिस्टिक्स लागत में 25% कमी।",
      "डिलीवरी समय में 30% सुधार।",
      "लॉजिस्टिक्स मैनेजमेंट सिस्टम पर 99.9% अपटाइम।",
    ],
    galleryCaptions: [
      "लैंडिंग पेज हीरो सेक्शन",
      "होम क्विक सर्विसेज़ सेक्शन",
      "चार्जिंग स्टेशन मैप",
      "सेवा बुकिंग के लिए मुख्य सेवाएँ पेज",
      "फ्रैंचाइज़ी और चार्जिंग स्टेशन आवेदन पेज",
      "EV स्टोर",
      "एडमिन डैशबोर्ड",
      "इन्वेंटरी मैनेजमेंट",
      "बारकोड जेनरेटर",
      "एडमिन बुक की गई सेवाओं की सूची",
      "फ्रैंचाइज़ी मैनेजमेंट",
      "फ्रैंचाइज़ी डैशबोर्ड",
      "फ्रैंचाइज़ी स्टॉक अनुरोध पेज",
      "तकनीशियन मैनेजमेंट",
      "EV सेवा बुकिंग चरण",
      "EV सेवा बुकिंग चरण",
      "पेमेंट गेटवे इंटीग्रेशन",
      "वेबसाइट कंटेंट मैनेजमेंट सिस्टम",
      "एडमिन यूज़र मैनेजमेंट",
    ],
  },
  "samku-ev-ecommerce": {
    title: "समकू EV ई-कॉमर्स",
    category: "ई-कॉमर्स",
    description:
      "EV बाज़ार में संस्थागत डिजिटल संपत्तियों के लिए अपरिवर्तनीय लेनदेन परतें।",
    solution: "डिस्ट्रिब्यूटेड लेजर",
    industry: "वित्तीय सेवाएँ",
    duration: "6 महीने",
    challenge:
      "EV घटकों के द्वितीयक बाज़ार में विश्वास और पारदर्शिता की कमी थी। संस्थागत खरीदारों को संपत्ति इतिहास और स्वामित्व सत्यापित करने का सुरक्षित तरीका चाहिए था।",
    approach:
      "हमने ब्लॉकचेन-समर्थित ई-कॉमर्स प्लेटफ़ॉर्म बनाया जो हर लेनदेन को अपरिवर्तनीय लेजर पर दर्ज करता है। निर्माता से अंतिम उपयोगकर्ता तक घटकों की पूर्ण ट्रैसेबिलिटी सुनिश्चित होती है।",
    results: [
      "सुरक्षित लेनदेन में $10M से अधिक सुविधा।",
      "बाज़ार से नकली घटक समाप्त।",
      "प्रमुख संस्थागत भागीदारों के साथ विश्वास बनाया।",
    ],
    galleryCaptions: [
      "लैंडिंग पेज",
      "उत्पाद विवरण पेज",
      "चार्जर उत्पाद विवरण पेज",
      "चार्जर्स पेज",
      "मुख्य उत्पाद श्रेणी पेज",
      "मुख्य उत्पाद पेज",
      "उत्पाद विवरण पेज",
      "एडमिन होम लैंडिंग पेज प्रबंधन",
      "एडमिन उत्पाद क्वेरी पेज",
    ],
  },
  educore: {
    title: "EduCore - स्कूल मैनेजमेंट सिस्टम",
    category: "मैनेजमेंट सिस्टम",
    description:
      "React Native और Node.js से बनी व्यापक, प्रोडक्शन-रेडी मोबाइल शिक्षा प्रबंधन ऐप। Google Classroom जैसी कक्षाएँ, Udemy-स्टाइल कोर्स और Google Forms-स्टाइल क्विज़।",
    solution: "मोबाइल एप्लिकेशन",
    industry: "सार्वजनिक स्कूल",
    duration: "1 वर्ष",
    challenge:
      "स्कूल को छात्रों, शिक्षकों और कोर्स प्रबंधित करने के लिए मोबाइल ऐप चाहिए था। बड़ी संख्या में छात्र, शिक्षक, कोर्स और असाइनमेंट संभालने की क्षमता ज़रूरी थी।",
    approach:
      "हमने स्केलेबिलिटी के लिए माइक्रोसर्विस-आधारित डिस्ट्रिब्यूटेड क्लाउड समाधान बनाया। बैकएंड में FastAPI-संचालित ML सेवाएँ शामिल हैं। मोबाइल ऐप में ऑफ़लाइन-प्रथम क्षमताएँ लागू कीं ताकि डेटा संग्रह और सिंक विश्वसनीय रहे।",
    results: [
      "ऐप के माध्यम से 100% छात्र जुड़ाव।",
      "ऐप के माध्यम से 90% शिक्षक उत्पादकता।",
      "ऐप के माध्यम से 95% कोर्स पूर्णता दर।",
    ],
    galleryCaptions: [
      "अकाउंट बनाएँ पेज",
      "लॉगिन पेज",
      "शिक्षक डैशबोर्ड",
      "शिक्षक छात्र सूची",
      "शिक्षक प्रोफ़ाइल पेज",
      "शिक्षक कोर्स स्क्रीन",
      "शिक्षक असाइनमेंट जोड़ स्क्रीन",
      "नया असाइनमेंट पेज",
      "छात्र जोड़ने की प्रक्रिया",
      "छात्र कोर्स स्क्रीन",
      "शिक्षक क्विज़ जोड़ स्क्रीन",
      "छात्र डैशबोर्ड",
      "नया असाइनमेंट जोड़ स्क्रीन",
      "एडमिन / HOD डैशबोर्ड",
      "मोबाइल ऐप स्क्रीन",
      "एडमिन एनालिटिक्स स्क्रीन",
      "शिक्षकों की सूची एडमिन स्क्रीन",
    ],
  },
  aquamrut: {
    title: "Aquamrut - जल उपचार संयंत्र प्रबंधन सिस्टम",
    category: "जल सेवाएँ",
    description:
      "Aquaamrut भारत भर में प्रिसिजन-इंजीनियर्ड शुद्धिकरण सिस्टम देने वाली प्रमुख औद्योगिक जल उपचार कंपनी है। कॉम्पैक्ट घरेलू RO यूनिट से बड़े औद्योगिक संयंत्र तक - उन्नत मेम्ब्रेन तकनीक और गहरी इंजीनियरिंग विशेषज्ञता।",
    solution: "वेब एप्लिकेशन",
    industry: "जल उपचार सेवाएँ",
    duration: "6 महीने",
    challenge:
      "Aquaamrut को जल उपचार संयंत्रों और सेवाओं के प्रबंधन के लिए वेब एप्लिकेशन चाहिए था। बड़ी संख्या में संयंत्र, सेवाएँ, ग्राहक और ऑर्डर संभालने की क्षमता ज़रूरी थी।",
    approach:
      "हमने वेब एप्लिकेशन बनाया जिससे Aquaamrut अपने जल उपचार संयंत्र और सेवाएँ प्रबंधित कर सके। डैशबोर्ड, नए संयंत्र/सेवा जोड़ने के फॉर्म और सभी संयंत्रों व सेवाओं की सूची शामिल है।",
    results: [
      "एप्लिकेशन से 100% संयंत्र प्रबंधन।",
      "एप्लिकेशन से 90% सेवा प्रबंधन।",
      "एप्लिकेशन से 95% ग्राहक प्रबंधन।",
    ],
    galleryCaptions: [
      "लैंडिंग पेज",
      "उत्पाद विवरण पेज",
      "जल सेवा पेज",
      "उत्पाद पेज",
      "समाधान पेज",
      "समाधान सूची पेज",
    ],
  },
};

function localizeGallery(
  gallery: GalleryItem[] | undefined,
  captions: string[] | undefined,
): GalleryItem[] | undefined {
  if (!gallery) return gallery;
  if (!captions) return gallery;
  return gallery.map((item, i) => ({
    ...item,
    caption: captions[i] ?? item.caption,
  }));
}

export function localizeProject(
  project: Project,
  lang: SiteLanguageCode,
): Project {
  if (lang !== "hi") return project;
  const overlay = projectsHi[project.id];
  if (!overlay) return project;

  return {
    ...project,
    title: overlay.title,
    category: overlay.category,
    description: overlay.description,
    solution: overlay.solution,
    industry: overlay.industry,
    duration: overlay.duration ?? project.duration,
    challenge: overlay.challenge,
    approach: overlay.approach,
    results: overlay.results,
    gallery: localizeGallery(project.gallery, overlay.galleryCaptions),
  };
}

export function localizeProjects(
  list: Project[],
  lang: SiteLanguageCode,
): Project[] {
  return list.map((p) => localizeProject(p, lang));
}

/** Map English category filter keys to display labels. */
export function localizeCategoryLabel(
  categoryEn: string,
  lang: SiteLanguageCode,
): string {
  if (lang !== "hi") return categoryEn;
  return categoryLabelsHi[categoryEn] ?? categoryEn;
}

/** All unique English category keys from the projects dataset. */
export function getProjectCategoryKeys(): string[] {
  return Array.from(new Set(projects.map((p) => p.category)));
}
